import React, { Component } from 'react'
import PostBuilder from './post-builder'
import { isAdmin } from '../../utils'
import axiosInstance, { wpAxios, wpAdminAjax } from '../../config/axiosResolver'
import { createSerialQueue, shouldRetryWarm, warmDelay } from './boardWarm'

class PostBuilderContainer extends Component {
  constructor(props) {
    super()

    this.state = {
      isLoading: true,
      submission: {},
      gallery: {},
      isAdmin: isAdmin(props.user),
      additionalInfo: {},
      // STY-625: a failed placement save used to vanish without a trace — the
      // editor kept placing images into a document that was not being stored.
      saveError: null
    }

    this.id = props.match.params.id
    // STY-625: sha1 of the stored document THIS TAB is working from, seeded
    // on mount and rolled forward from each save's response. Sent as baseHash
    // on every save, so a Post Builder left open while the document moved on
    // (another window, the single-submission view) gets a 409 instead of
    // silently reverting the newer work — the exact accident, in the other
    // direction, that wiped submission 382708's image placements.
    this.baseHash = null
    // STY-625: latched on a 409. Every save posts the whole document, so once
    // one is refused as stale there is nothing useful left to send.
    this.staleDoc = false
    // STY-625: saves run one after another. They used to fire concurrently,
    // and since each one replaces the entire document, two in-flight saves
    // could land out of order and drop a placement — and with baseHash they
    // would instead trip the stale guard on their own sibling.
    this.saveChain = Promise.resolve()
    // STY-625: the one authoritative copy of the document. Every mutation
    // composes on this, never on a prop a child was handed at render time —
    // see saveSubmission().
    this.doc = {}
    this.saveSubmission = this.saveSubmission.bind(this)
    this.createBoard = this.createBoard.bind(this)
    // STY-635: one board warm in flight at a time — see boardWarm.js.
    this.enqueueWarm = createSerialQueue()
  }

  /**
   * Apply a change to the document and save the result.
   *
   * Takes a function, not a document. Callers used to mutate the
   * `additionalInfo` prop they had been handed and post that object back, but a
   * prop is a snapshot from render time: any handler that awaits something
   * first — and createBoard's retries run for two to three seconds — is holding
   * a document from before whatever landed in between, and posting it reverts
   * that work.
   *
   * Not hypothetical. On 2026-08-25 submission 383399 lost a placement to two
   * rollbacks racing on two separately captured objects: one reverted an image
   * the other had just added, and for two seconds the stored document was
   * missing an image nobody had asked to remove.
   *
   * The baseHash guard below does not catch this, and cannot. It rolls forward
   * from every save's response, so a save built on a stale document still
   * carries a current hash — it is the content that is behind, not the
   * baseline.
   */
  saveSubmission (mutate) {
    // Applied immediately rather than inside the chain, so the change is on
    // screen before the request goes out and the next mutation composes on top
    // of this one instead of on whatever the caller was holding.
    this.doc = mutate(this.doc) || this.doc

    // A fresh top-level object, so React re-renders and ImageContainer's effect
    // re-syncs. Nested values are deliberately shared with this.doc: this copy
    // exists to be read, and saveSubmission is now the only writer.
    this.setState({ additionalInfo: { ...this.doc } })

    // What this particular action produced. The chain can hold a save behind an
    // earlier one for a second or more, and this.doc keeps moving meanwhile —
    // sending it by reference would post a later state under an earlier
    // action's revision.
    const payload = JSON.parse(JSON.stringify(this.doc))

    this.saveChain = this.saveChain.then(() => this.postSave(payload))
    return this.saveChain
  }

  // Never rejects — a failed save must not stall the chain behind it.
  postSave (data) {
    if (this.staleDoc) return Promise.resolve()

    return wpAxios.post(`https://www.stylemepretty.com/api/v2/submissions/${this.id}/save-additional-info`, this.baseHash ? { data: data, baseHash: this.baseHash } : { data: data })
      .then(res => {
        this.baseHash = (res.data && res.data.baseHash) || this.baseHash
        // Deliberately NOT adopting res.data.data.data as the document. The
        // response echoes what this request stored, which is already behind
        // anything the editor did while it was in flight — taking it would be
        // the same "older document wins" mistake this method exists to stop.
        if (this.state.saveError) this.setState({ saveError: null })
      })
      .catch(err => {
        if (err.response && err.response.status === 409) {
          this.staleDoc = true
          this.setState({ saveError: 'This submission was updated in another tab or window. Your image placements are NOT being saved — reload this page to pick up the latest version before continuing.' })
        } else {
          this.setState({ saveError: 'Save failed — your last change was NOT saved. Check your connection and try again.' })
        }
      })
  }

  // Warm the board for an image. Fire-and-forget from the caller's point of
  // view: it resolves or rejects, but nothing about the document depends on
  // which (STY-635). Serialized so a burst of drops cannot fan out into a
  // burst of multi-second image-server jobs.
  createBoard (imageId) {
    return this.enqueueWarm(() => this.warmBoard(imageId))
  }

  warmBoard (imageId, attempt = 0) {
    return wpAdminAjax
      .post(`https://www.stylemepretty.com/wp-admin/admin-ajax.php?action=smp_create_board&imageId=${imageId}&userId=${this.props.user.id}&submissionId=${this.id}`)
      .catch((err) => {
        if (!shouldRetryWarm(err, attempt)) throw err
        return new Promise((resolve, reject) => {
          setTimeout(
            () => this.warmBoard(imageId, attempt + 1).then(resolve, reject),
            warmDelay(attempt)
          )
        })
      })
  }

  componentDidMount () {
    if(!this.state.isAdmin) this.props.history.push(`/submissions/dashboard/${this.props.user.email}`)
    Promise.all([
      axiosInstance.get(`/submissions/${this.id}/`),
      wpAxios.get(`https://www.stylemepretty.com/api/v2/submissions/${this.id}/get-additional-info`)
    ])
      .then(([submission, additionalInfo]) => {
        // STY-625: the document this tab is now working from. Null when no
        // document exists yet or the server predates the handshake; saves
        // then omit baseHash and the server skips the check.
        this.baseHash = (additionalInfo.data && additionalInfo.data.baseHash) || null
        // The only place this.doc is ever set from the server. After this it
        // moves only through saveSubmission().
        this.doc = additionalInfo.data.data.data
        if(submission.data.gallery_id) {
          wpAxios.get(`https://www.stylemepretty.com/api/v2/gallery/${submission.data.gallery_id}/post-builder`)
            .then((res) => {
              this.setState({
                submission: submission.data,
                additionalInfo: { ...this.doc },
                gallery: res.data.data,
                isLoading: false
              })
            })
        }
      })
  }

  render () {
    return (
      <PostBuilder
        {...this.state}
        saveSubmission={this.saveSubmission}
        createBoard={this.createBoard}
      />
    )
  }
}

export default PostBuilderContainer

